import type { FieldSize } from './constants';

export type Key = 'left' | 'right';
export type KeyAction = 'down' | 'up';

export type BotDifficulty = 'easy' | 'normal' | 'hard';

export interface RoomSettings {
  fieldSize: FieldSize;
  botDifficulty: BotDifficulty;
}

export type PlayerRole = 'player' | 'spectator';

export interface PlayerInfo {
  id: string;
  nickname: string;
  role: PlayerRole;
  connected: boolean;
  isHost: boolean;
}

export type EntrantKind = 'human' | 'bot';

/** Un participant au tournoi (humain ou bot). */
export interface Entrant {
  id: string;
  name: string;
  kind: EntrantKind;
  /** Identifiant du joueur du salon si `kind === 'human'`. */
  playerId: string | null;
}

export interface HeatResult {
  entrantId: string;
  /** Classement dans la série, à partir de 1. */
  rank: number;
  /** Temps d'arrivée, `null` si le coureur n'a pas fini (DNF). */
  timeMs: number | null;
  distanceM: number;
  falls: number;
  qualified: boolean;
}

export type HeatStatus = 'pending' | 'live' | 'done';

/** Une série de course (au plus 8 coureurs). */
export interface Heat {
  id: string;
  roundIndex: number;
  index: number;
  /** Coureurs par couloir (l'index du tableau est le numéro de couloir - 1). Vide tant que non déterminé. */
  lanes: Entrant[];
  status: HeatStatus;
  results: HeatResult[] | null;
}

export interface Round {
  index: number;
  name: string;
  heats: Heat[];
}

export type TournamentStep = 'briefing' | 'countdown' | 'racing' | 'results' | 'finished';

export interface TournamentState {
  rounds: Round[];
  roundIndex: number;
  step: TournamentStep;
  /** Temps restant avant la fin de l'étape courante, mesuré à l'émission. */
  stepRemainingMs: number | null;
  championId: string | null;
}

export interface RoomState {
  code: string;
  hostId: string;
  settings: RoomSettings;
  players: PlayerInfo[];
}

export interface StepPayload {
  roundIndex: number;
  step: TournamentStep;
  stepRemainingMs: number | null;
}

export type RunnerPhase = 'idle' | 'running' | 'fallen' | 'finished';
export const RUNNER_PHASES: readonly RunnerPhase[] = ['idle', 'running', 'fallen', 'finished'];

/**
 * État compact d'un coureur dans un snapshot réseau :
 * `[distanceM, vitesse m/s, indexPhase (RUNNER_PHASES), chutes, tempsArrivéeMs (-1 si en course)]`.
 */
export type RunnerSnapshot = readonly [
  distanceM: number,
  speed: number,
  phase: number,
  falls: number,
  finishTimeMs: number,
];

export interface RaceSnapshot {
  heatId: string;
  /** Temps de course serveur (ms), négatif pendant le décompte. */
  t: number;
  /** Un élément par couloir, dans l'ordre des couloirs. */
  runners: RunnerSnapshot[];
}
